<script setup lang="ts">
import { type Ref, computed, inject, ref } from 'vue';
import { useMutation, useQuery } from '@tanstack/vue-query';
import {
  type CandidateLine, erpIntegrationApi, type SessionList, type SessionRow,
} from '@/api/erp-integration';
import { apiErrorCode } from '@/utils/api-errors';
import { careerLabel } from '@/utils/career-metadata';
import { formatCurrency } from '@/utils/currency';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import type { SerializedUser } from '@/api/users';
import { actionError, sessionCode, sessionConflict } from './erp-messages';
import { formatDate, formatDateTime, formatRange, quantityText, requirementText } from './erp-format';

// Kuido's Sessions in Odoo that need a coordinator: Shifts without a
// Sales order line (pick one), Sessions Odoo refused or someone edited by hand
// (send again), and Sessions typed by hand on incorporated patients' lines.

const props = defineProps<{ list: SessionList }>();
const emit = defineEmits<{ changed: [message?: string] }>();

/* eslint-disable camelcase */
const INTRO: Record<SessionList, { title: string; text: string }> = {
  without_line: {
    title: 'Turnos sin presupuesto',
    text: 'Turnos de pacientes incorporados para los que Kuido no sabe en qué línea de ' +
      'presupuesto registrar la sesión: no hay una que cubra la fecha, o hay más de una. Elige ' +
      'la línea y Kuido envía la sesión. Un turno realizado que quedó fuera de las fechas del ' +
      'presupuesto mantiene su sesión y su pago hasta que elijas otra línea o Odoo corrija las ' +
      'fechas.',
  },
  problems: {
    title: 'Sesiones con problemas',
    text: 'Sesiones que Odoo rechazó, que alguien editó a mano en Odoo (Kuido las vuelve a ' +
      'dejar como corresponde) o cuyo pago bruto o línea Odoo calculó distinto.',
  },
  adoption: {
    title: 'Sesiones a mano',
    text: 'Sesiones tipeadas a mano en Odoo para pacientes incorporados, después de su ' +
      'incorporación: Kuido ya registra esos turnos, así que pueden pagar una visita dos veces. ' +
      'Revísalas y elimínalas en Odoo si corresponde.',
  },
};
/* eslint-enable camelcase */

const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 25 });
const currentUser = inject<Ref<SerializedUser> | undefined>('currentUser', undefined);
const timeZone = computed(() => currentUser?.value?.companyTimeZone);

const { data: page, isPending } = useQuery({
  queryKey: ['erpIntegration', 'sessions', computed(() => props.list)],
  queryFn: () => erpIntegrationApi.sessions(props.list),
});
const rows = computed(() => page.value?.rows ?? []);

const headers = computed(() => {
  const shiftHeaders = [
    { title: 'Turno', key: 'shift', sortable: false },
    { title: 'Paciente', key: 'patient', sortable: false },
    { title: 'Profesional', key: 'caregiver', sortable: false },
  ];
  if (props.list === 'without_line') {
    return [...shiftHeaders, { title: 'Motivo', key: 'code', sortable: false },
      { title: 'Línea del presupuesto', key: 'pick', sortable: false }];
  }
  if (props.list === 'problems') {
    return [...shiftHeaders, { title: 'Problema', key: 'code', sortable: false },
      { title: '', key: 'actions', sortable: false, align: 'end' as const }];
  }

  return [...shiftHeaders, { title: 'Sesiones candidatas en Odoo', key: 'candidates',
    sortable: false }];
});

const unclaimedHeaders = [
  { title: 'Fecha', key: 'date' },
  { title: 'Paciente', key: 'patient', sortable: false },
  { title: 'Profesional', key: 'caregiver', sortable: false },
  { title: 'Cantidad', key: 'quantity' },
  { title: 'Pago', key: 'pay' },
  { title: 'Sesión en Odoo', key: 'remoteId' },
];

const picks = ref<Record<number, number | null>>({});
const rowError = ref<Record<number, string>>({});

// Two lines of one product read apart by what was sold and the Requirement each
// feeds: "S04548 · KINE · 12 Sesiones · Kinesiólogo/a · 3 por semana · 45 min (…)".
function lineTitle(line: CandidateLine): string {
  const parts = [line.reference ?? 'Presupuesto', line.productName,
    quantityText(line.quantity, line.uom), line.requirement ? requirementText(line.requirement) : null];
  return `${parts.filter(Boolean).join(' · ')} (${formatRange(line.startsOn, line.endsOn)})`;
}

function driftText(drift: string[]): string {
  return drift.includes('missing') ? 'Eliminada o movida a mano en Odoo; Kuido la repuso' :
    `Editada a mano en Odoo (${drift.join(', ')}); Kuido la corrigió`;
}

function checkText(row: SessionRow): string {
  if (row.lineCheck) return 'Odoo la cuenta en otra línea del presupuesto';
  if (!row.payCheck) return 'Por revisar';

  return `Odoo calculó otro bruto (${formatCurrency(row.payCheck.erp.grossCost)}; Kuido ` +
    `${formatCurrency(row.payCheck.expected.grossCost)})`;
}

function problemText(row: SessionRow): string {
  if (row.status === 'failed') return sessionCode(row.code);

  return row.drift?.length ? driftText(row.drift) : checkText(row);
}

function codeText(row: SessionRow): string {
  if (props.list === 'problems') return problemText(row);

  return row.conflict ? sessionConflict(row.conflict) : sessionCode(row.code);
}

const { mutate: pick, isPending: isPicking, variables: picking } = useMutation({
  mutationFn: ({ row, lineId }: { row: SessionRow; lineId: number }) => (
    erpIntegrationApi.pickLine(row.id, lineId)
  ),
  onSuccess: () => emit('changed', '¡Línea asignada! La sesión se envía en unos minutos.'),
  onError: (error, { row }) => {
    rowError.value = { ...rowError.value, [row.id]: actionError(apiErrorCode(error),
      'Ocurrió un error al asignar la línea. Intenta nuevamente.') };
  },
});

const { mutate: retry, isPending: isRetrying, variables: retrying } = useMutation({
  mutationFn: (row: SessionRow) => erpIntegrationApi.retry(row.id),
  onSuccess: () => emit('changed', '¡Sesión en cola! Se envía en unos minutos.'),
  onError: (error, row) => {
    rowError.value = { ...rowError.value, [row.id]: actionError(apiErrorCode(error),
      'Ocurrió un error al reenviar. Intenta nuevamente.') };
  },
});
</script>
<template>
  <div class="pa-4">
    <h2 class="text-h6 mb-1">
      {{ INTRO[list].title }}
    </h2>
    <p class="mb-4 text-body-2 text-medium-emphasis">
      {{ INTRO[list].text }}
    </p>
    <p
      v-if="page?.total && page.total > rows.length"
      class="mb-2 text-body-2"
    >
      Se muestran los {{ rows.length }} primeros de {{ page.total }}.
    </p>
    <h3
      v-if="list === 'adoption'"
      class="text-subtitle-1 font-weight-bold mb-2"
    >
      Turnos con más de una sesión a mano que podría ser suya
    </h3>
    <v-data-table
      v-bind="tableProps"
      :header-props="{ class: 'font-weight-bold' }"
      :headers="headers"
      :items="rows"
      :loading="isPending"
      loading-text="Cargando las sesiones..."
      no-data-text="No hay sesiones en esta lista"
      density="compact"
    >
      <template #[`item.shift`]="{ item }">
        <template v-if="item.shift">
          <div>{{ formatDateTime(item.shift.startsAt, timeZone) }}</div>
          <v-chip
            v-if="item.shift.suspended"
            size="x-small"
            label
          >
            Suspendido
          </v-chip>
        </template>
        <span
          v-else
          class="text-medium-emphasis"
        >
          {{ item.code === 'orphan_session' ? `Sesión ${item.remoteId} sin turno` : 'Turno eliminado' }}
        </span>
      </template>
      <template #[`item.patient`]="{ item }">
        {{ item.shift?.patient?.name ?? '—' }}
      </template>
      <template #[`item.caregiver`]="{ item }">
        <div>{{ item.shift?.caregiver?.name ?? '—' }}</div>
        <div
          v-if="item.shift?.caregiver"
          class="text-caption text-medium-emphasis"
        >
          {{ careerLabel(item.shift.caregiver.career) }}
        </div>
      </template>
      <template #[`item.code`]="{ item }">
        <v-chip
          size="small"
          :color="item.status === 'failed' ? 'error' : 'warning'"
          :prepend-icon="item.status === 'failed' ? 'mdi-close-circle' : 'mdi-alert-circle'"
        >
          {{ codeText(item) }}
        </v-chip>
        <div
          v-if="item.conflict"
          class="text-caption text-medium-emphasis"
        >
          La sesión enviada mantiene su pago
        </div>
        <div
          v-if="item.attempts"
          class="text-caption text-medium-emphasis"
        >
          {{ item.attempts === 1 ? '1 intento' : `${item.attempts} intentos` }}
        </div>
        <div
          v-if="list !== 'without_line' && rowError[item.id]"
          class="text-caption text-error"
          role="alert"
        >
          {{ rowError[item.id] }}
        </div>
      </template>
      <template #[`item.pick`]="{ item }">
        <div class="d-flex flex-wrap align-center ga-2 py-1">
          <v-select
            v-model="picks[item.id]"
            :items="(item.candidates ?? []).map((line) => ({ value: line.id, title: lineTitle(line) }))"
            :label="(item.candidates ?? []).length ? 'Línea' : 'Ninguna línea cubre la fecha'"
            :disabled="!(item.candidates ?? []).length"
            variant="outlined"
            density="compact"
            hide-details
            class="line-select"
          />
          <v-btn
            size="small"
            color="primary"
            variant="tonal"
            :disabled="!picks[item.id]"
            :loading="isPicking && picking?.row.id === item.id"
            @click="pick({ row: item, lineId: picks[item.id] as number })"
          >
            Asignar
          </v-btn>
        </div>
        <div
          v-if="rowError[item.id]"
          class="text-caption text-error"
          role="alert"
        >
          {{ rowError[item.id] }}
        </div>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn
          v-if="item.shift || item.code !== 'orphan_session'"
          size="small"
          variant="tonal"
          color="primary"
          :loading="isRetrying && retrying?.id === item.id"
          @click="retry(item)"
        >
          Reenviar
        </v-btn>
      </template>
      <template #[`item.candidates`]="{ item }">
        {{ (item.candidateSessionIds ?? []).map((id) => `#${id}`).join(', ') }}
      </template>
    </v-data-table>
    <template v-if="list === 'adoption'">
      <h3 class="text-subtitle-1 font-weight-bold mt-6 mb-2">
        Sesiones a mano en pacientes incorporados
      </h3>
      <p
        v-if="page?.unclaimedTotal && page.unclaimedTotal > (page.unclaimed?.length ?? 0)"
        class="mb-2 text-body-2"
      >
        Se muestran las {{ page.unclaimed?.length }} más antiguas de {{ page.unclaimedTotal }}.
      </p>
      <v-data-table
        v-bind="tableProps"
        :header-props="{ class: 'font-weight-bold' }"
        :headers="unclaimedHeaders"
        :items="page?.unclaimed ?? []"
        :loading="isPending"
        loading-text="Cargando las sesiones..."
        no-data-text="No hay sesiones a mano según la última reconciliación"
        density="compact"
      >
        <template #[`item.date`]="{ item }">
          {{ formatDate(item.date) }}
        </template>
        <template #[`item.patient`]="{ item }">
          {{ item.patient?.name ?? '—' }}
        </template>
        <template #[`item.caregiver`]="{ item }">
          {{ item.caregiver?.name ?? '—' }}
        </template>
        <template #[`item.pay`]="{ item }">
          {{ formatCurrency(item.pay) }}
        </template>
        <template #[`item.remoteId`]="{ item }">
          #{{ item.remoteId }}
        </template>
      </v-data-table>
    </template>
  </div>
</template>
<style scoped>
.line-select {
  min-width: 0;
}

@media (min-width: 600px) {
  .line-select {
    min-width: 260px;
  }
}
</style>
